import { useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import {
  faPlusCircle,
  faSignOutAlt,
  faUser,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useAuth } from "@api";
import { useBasePath } from "@hooks";
import { OrgContext, UserContext } from "@providers";
import { Feature } from "@utils";

import { replaceOrgInPath } from "@pages/dashboard/utils";

const Container = styled.div`
  background: #3a115c;
  border: 1px solid #644980;
  margin-right: 12px;
  box-sizing: border-box;
  border-radius: 3px;
  width: 100%;
`;

const OrgSection = styled.section`
  padding: 8px 12px 6px 8px;
  user-select: none;

  &:hover {
    background: rgba(242, 248, 255, 0.12);
    cursor: pointer;
  }
  &:active {
    background: rgba(242, 248, 255, 0.15);
    cursor: pointer;
  }
`;

const OrgNameSection = styled.section`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const OrgName = styled.span`
  font-size: var(--global-font-size-s);
  & {
    max-width: 160px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const TierSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
`;

const TierText = styled.span`
  font-size: var(--global-font-size-xs);
  color: var(--global-green);
`;

const OrgListSection = styled.ul`
  margin: 0;
  border-top: 1px solid rgba(242, 248, 255, 0.2);
  padding: 8px 9px 6px 8px;
  list-style: none;

  & a {
    color: var(--global-white);
    text-decoration: none;
  }
`;

const OrgListItem = styled.li`
  font-size: var(--global-font-size-s);
  border-radius: 2px;
  & {
    max-width: 160px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &:hover {
    background: rgba(242, 248, 255, 0.12);
    overflow: visible;
  }
`;

const ActionsSection = styled.ul`
  margin: 0;
  border-top: 1px solid rgba(242, 248, 255, 0.2);
  padding: 8px 9px 6px 8px;
  list-style: none;

  & a {
    color: var(--global-white);
    text-decoration: none;
  }
`;

// luke would be proud about our action items
const ActionItem = styled.li`
  font-size: var(--global-font-size-s);
  border-radius: 2px;
  cursor: pointer;

  &:hover {
    background: rgba(242, 248, 255, 0.12);
  }
`;

export const OrgPicker: React.FC<{ hidden?: boolean }> = ({
  hidden = false,
}) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const { org, setOrg } = useContext(OrgContext);
  const { user } = useContext(UserContext);
  const { install, logout } = useAuth();
  const history = useHistory();
  const multipleOrgs = user !== undefined && user.orgs.length > 1;
  const basePath = useBasePath();

  return (
    <Container>
      <OrgSection onClick={() => setExpanded(!expanded)}>
        <OrgNameSection>
          <OrgName>{org?.name ?? "Loading organization"}</OrgName>
        </OrgNameSection>
        {expanded && !hidden && (
          <TierSection>
            <TierText>
              {Feature.hasTeamsTierTrial({ org })
                ? "Team trial"
                : Feature.hasTeamsTier({ org })
                ? "Team tier"
                : "Community"}
            </TierText>
          </TierSection>
        )}
      </OrgSection>
      {expanded && !hidden && (
        <>
          {multipleOrgs && (
            <OrgListSection>
              {user!.orgs
                .filter((userOrg) => userOrg.id !== org?.id)
                .map((newOrg) => (
                  <ActionItem
                    key={newOrg.name}
                    onClick={() => {
                      setOrg(newOrg);
                      history.push(
                        replaceOrgInPath(history.location.pathname, newOrg.name)
                      );
                      setExpanded(false);
                    }}
                  >
                    <OrgListItem>{newOrg.name}</OrgListItem>
                  </ActionItem>
                ))}
            </OrgListSection>
          )}
          <ActionsSection>
            {user !== undefined &&
              ["gitlab", "github"].some(
                (item) => item === user!.auth_provider_type
              ) && (
                <ActionItem
                  onClick={async () => {
                    await install(user.auth_provider_type);
                  }}
                >
                  <FontAwesomeIcon icon={faPlusCircle} fixedWidth /> Add org
                </ActionItem>
              )}
            <ActionItem
              onClick={() => {
                history.push(`${basePath}/settings/account`);
                setExpanded(false);
              }}
            >
              <FontAwesomeIcon icon={faUser} fixedWidth /> Account
            </ActionItem>
            <ActionItem onClick={logout}>
              <FontAwesomeIcon icon={faSignOutAlt} fixedWidth /> Sign out
            </ActionItem>
          </ActionsSection>
        </>
      )}
    </Container>
  );
};
